import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
  ImageSplitFormSection,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import {
  activateCardConstants,
  phoneMaxLength,
} from '../../../utils/constants/activate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { useEffect, useState } from 'react';
import { IconSectionCard } from '../../../shared/iconSectionCardSection';
import axios from 'axios';
import { globalConstants } from '../../../utils/constants/global';
import { GetEftposInputs } from './sections/GetEftposInputs';
import { OTPModal } from '../../modals';

const {
  activationCode,
  firstName,
  lastName,
  phoneNumber,
  termsConditionsCheck,
} = activateCardConstants.validations;

type ModalState =
  | { phone: string | null; open: false; token: string | null }
  | {
      phone: string;
      token: string | null;
      open: true;
      resolve: (result: boolean) => void;
    };

export const GetEftpos = () => {
  const dispatch = useContextDispatch();
  const { loading, otpToken } = useContextState();

  const [serverErrors, setServerErrors] = useState('');
  const [modalState, setModalState] = useState<ModalState>({
    phone: null,
    open: false,
    token: null,
  });

  const { expiredCardMessage } = globalConstants;
  const { push } = useHistory();

  const detailFormMethods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      firstName: '',
      lastName: '',
      eaid: '',
      phoneNumber: '',
      addressLine1: '',
      city: '',
      postcode: '',
      state: '',
      termsConditionsCheck: false,
    },
  });

  const handleClose = () => {
    if (modalState.open) {
      modalState.resolve(true);
      setModalState({ phone: '', open: false, token: null });
    }
  };

  const authenticate = async (phone: string) => {
    try {
      const data = {
        mobile_number: phone,
      };
      await axios.post('/v1/requestotp', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || '',
        },
      });
      await new Promise((resolve) => {
        setModalState({ open: true, token: null, phone: phone, resolve });
      });
    } catch (err: any) {
      if (err?.response?.status === 400 || err?.response?.status === 401) {
        setServerErrors(err?.response?.data?.message);
      } else {
        setServerErrors('Something went wrong');
      }
    }
  };

  const authHeader = (token: string) => {
    return {
      Authorization: `Bearer ${token}`,
      'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || '',
    };
  };

  const onSubmit = async (formData: any) => {
    if (!otpToken) {
      dispatch('START_LOADING');
      setServerErrors('');
      await authenticate(formData.phoneNumber);
      dispatch('STOP_LOADING');
      return;
    }

    try {
      dispatch('START_LOADING');
      setServerErrors('');
      const data = {
        addressLine1: formData.addressLine1,
        city: formData.city,
        eAID: formData.eaid,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phoneNumber,
        postcode: formData.postcode,
        state: formData.state,
      };
      await axios.post('/v1/exchangecard', data, {
        headers: authHeader(otpToken),
      });
      dispatch('STOP_LOADING');
      push({
        pathname: '/success',
        state: {
          message:
            'Thank you, we have successfully received your exchange request. Your physical card will be sent in the mail and you should receive it **within 20 business** days. To check the status of your exchange at any time contact info@vaultps.com.au.',
        },
      });
    } catch (err: any) {
      if (err?.response?.status === 400) {
        setServerErrors(err?.response?.data?.message);
      } else if (err?.response?.status === 700) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors('Something went wrong');
      }
      dispatch('STOP_LOADING');
    }
  };

  useEffect(() => {
    if (otpToken) {
      detailFormMethods.handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpToken]);

  const _renderForm = () => {
    return (
      <FormProvider {...detailFormMethods}>
        <Form onSubmit={detailFormMethods.handleSubmit(onSubmit)}>
          <SectionContainer>
            <Markdown children="# Exchange for Eftpos" />
            <Markdown children="Enter your request Details Below:" />
            <FormContainer>
              <FormSectionContainer className="activateCardSection">
                <GetEftposInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ButtonGroupContainer>
                  <Button
                    onClick={() => {
                      detailFormMethods.handleSubmit(onSubmit);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : 'Exchange Card'}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
              <FormSectionContainer>
                {process.env.REACT_APP_CARD_OR_ICONS === 'CARD' ? (
                  <ImageSplitFormSection
                    src={process.env.REACT_APP_CARD_FRONT ?? ''}
                  />
                ) : (
                  <IconSectionCard />
                )}
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    );
  };

  return (
    <>
      <Line />
      {modalState.open && (
        <OTPModal {...modalState} handleClose={handleClose} />
      )}
      {_renderForm()}
    </>
  );
};

const validationSchema = Yup.object().shape({
  eaid: Yup.string()
    .required(activationCode.required)
    .matches(/^[a-zA-Z0-9_.-]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  firstName: Yup.string()
    .required(firstName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: firstName.format,
      excludeEmptyString: true,
    }),
  lastName: Yup.string()
    .required(lastName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: lastName.format,
      excludeEmptyString: true,
    }),
  phoneNumber: Yup.string()
    .required(phoneNumber.required)
    .matches(/^(0)[0-9]+$/, {
      message: phoneNumber.format,
      excludeEmptyString: true,
    })
    .min(phoneMaxLength, phoneNumber.minLength)
    .max(phoneMaxLength, phoneNumber.maxLength),
  confirmPhoneNumber: Yup.string().oneOf(
    [Yup.ref('phoneNumber'), null],
    "Mobiles numbers don't match"
  ),

  addressLine1: Yup.string().required('Street address is required'),
  city: Yup.string()
    .required('City is required')
    .matches(/^[A-Za-z ]*$/, {
      message: 'City is invalid',
      excludeEmptyString: true,
    }),
  postcode: Yup.string()
    .required('Postcode is required')
    .matches(/^[0-9]*$/, {
      message: 'Postcode is invalid',
      excludeEmptyString: true,
    }),
  state: Yup.string()
    .required('State is required')
    .matches(/^[A-Za-z]*$/, {
      message: 'State is invalid',
      excludeEmptyString: true,
    }),
  termsConditionsCheck: Yup.bool().oneOf([true], termsConditionsCheck.required),
});
